/**
 * Real-device GPS debug mode.
 *
 * Off by default. Enable on a phone with `?gpsdebug=1` (persists for the
 * session) or from the console with `window.landooGps.enable()`.
 *
 * Records every raw reading and every centering decision, so a tester can
 * compare what the device reported against what the map actually did.
 */

export type GpsLogKind = "reading" | "decision" | "error" | "status";

export interface GpsLogEntry {
  t: number;
  kind: GpsLogKind;
  message: string;
  /** Raw, unrounded values exactly as reported by the device. */
  lat?: number;
  lng?: number;
  accuracy?: number;
  detail?: Record<string, unknown>;
}

const MAX_ENTRIES = 200;
const KEY = "landoo:gps-debug";

let enabled = false;
let entries: GpsLogEntry[] = [];
const listeners = new Set<(e: GpsLogEntry[]) => void>();

function emit() {
  const snapshot = entries.slice();
  listeners.forEach((l) => l(snapshot));
}

export function isGpsDebugEnabled() {
  return enabled;
}

export function setGpsDebug(next: boolean) {
  enabled = next;
  if (typeof window !== "undefined") {
    if (next) window.sessionStorage.setItem(KEY, "1");
    else window.sessionStorage.removeItem(KEY);
  }
  emit();
}

/** Reads `?gpsdebug=1` / sessionStorage once on the client. */
export function initGpsDebug() {
  if (typeof window === "undefined") return false;
  const fromUrl = new URLSearchParams(window.location.search).get("gpsdebug");
  const on = fromUrl === "1" || window.sessionStorage.getItem(KEY) === "1";
  if (on) setGpsDebug(true);
  const w = window as unknown as { landooGps?: unknown };
  w.landooGps = {
    enable: () => setGpsDebug(true),
    disable: () => setGpsDebug(false),
    log: () => entries.slice(),
    clear: clearGpsLog,
  };
  return on;
}

export function gpsLog(entry: Omit<GpsLogEntry, "t">) {
  if (!enabled) return;
  const full: GpsLogEntry = { t: Date.now(), ...entry };
  entries = [full, ...entries].slice(0, MAX_ENTRIES);
  // Raw values, never rounded — this is the device-truth record.
  console.info(
    `[landoo:gps] ${full.kind} · ${full.message}`,
    full.lat != null ? { lat: full.lat, lng: full.lng, accuracy: full.accuracy } : {},
    full.detail ?? {},
  );
  emit();
}

export function clearGpsLog() {
  entries = [];
  emit();
}

export function subscribeGpsLog(fn: (e: GpsLogEntry[]) => void) {
  listeners.add(fn);
  fn(entries.slice());
  return () => {
    listeners.delete(fn);
  };
}
